import { publishQueries } from '@/entities/sets/publish';
import { commonQueries, usePersistStore } from '@/shared/lib/store';
import { PodHeader } from '@/widgets/section-header';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { PodPreview } from '@/widgets/pod';
import { z } from 'zod';

const podPreviewSearchSchema = z.object({
    podData: z.string().optional(),
});

// LEGACY(POD1): 다시 발행 흐름이 사용 중이므로 POD 2 전환 전까지 유지합니다.
export const Route = createFileRoute('/sets/pod/preview/$publishGrpId')({
    validateSearch: (search) => podPreviewSearchSchema.parse(search),
    component: RouteComponent,
    loader: ({ params }) => {
        return { publishGrpId: Number(params.publishGrpId) };
    },
});

function RouteComponent() {
    const { podData: dataParam } = Route.useSearch();
    const { publishGrpId } = Route.useLoaderData();
    const { sUserId } = usePersistStore();
    const wsRef = useRef<WebSocket | null>(null);

    const [selectExam, setSelectExam] = useState<{
        value: string;
        label: string;
    }>();
    const [articleIdList, setArticleIdList] = useState<{ id: string }[]>([]);
    const [queryData, setQueryData] = useState<DATA.WsQueryDataType>();
    const [examList, setExamList] = useState<{ value: string; label: string }[]>([]);

    const { data } = publishQueries.publish.getIntegPubCompleteList(
        {
            publishGrpId: publishGrpId,
        },
        !!publishGrpId
    );
    const { data: publishData } = publishQueries.detail.getPublishDetail(
        { publishId: data?.examList[0]?.publishList[0]?.publishId!, wrterId: sUserId },
        !!data && !!sUserId,
        false
    );

    const { mutate } = commonQueries.postLesnRscSetSummaryList();

    const getArticleList = () => {
        mutate(
            {
                set_ids: [selectExam?.value?.split('examId')[0]!],
                orderby_col: 'id',
                orderby_dir: 'asc',
                limit_page: '1',
                limit_pageItemCnt: 100,
            },
            {
                onSuccess: (res) => {
                    if (res.status === 200) {
                        setArticleIdList(
                            res.data.vo[0].setSummary.map((e) => ({ id: e.article_id }))
                        );
                    }
                },
            }
        );
    };

    // 발행 완료 후 리스트의 첫번째 값으로 세팅
    useEffect(() => {
        if (data && data?.examList) {
            setExamList(
                data?.examList.map((e) => ({
                    value: e?.setsId + 'examId' + e?.examId,
                    label: e?.examNm,
                }))
            );
            setSelectExam &&
                setSelectExam({
                    value: data?.examList[0]?.setsId + 'examId' + data?.examList[0]?.examId,
                    label: data?.examList[0]?.examNm,
                });
        }
    }, [data]);

    // 발행 완료후 자료 선택에 따라 setId로 articleList 가져옴
    useEffect(() => {
        if (selectExam?.value) {
            getArticleList();
        }
    }, [selectExam]);

    useEffect(() => {
        if (
            publishData &&
            publishData?.publishInfoDetail?.podOptionList &&
            !!selectExam &&
            articleIdList?.length > 0
        ) {
            const legacy = publishData?.publishInfoDetail.podOptionList;

            const newCheckList = [];
            if (legacy.find((item) => item.code === 'pod_answer')?.val === 'Y') {
                newCheckList.push('pod_answer');
            }
            if (legacy.find((item) => item.code === 'pod_description')?.val === 'Y') {
                newCheckList.push('pod_description');
            }
            if (legacy.find((item) => item.code === 'pod_eng_description')?.val === 'Y') {
                newCheckList.push('pod_eng_description');
            }
            const uniqueItems =
                Array.from(new Map(articleIdList?.map((item) => [item.id, item]))?.values()) ?? '';
            const newData = {
                request: 'pdf',
                target: publishData?.publishInfoDetail?.curriSchoolVal.replace('학교', '') ?? '',
                header: {
                    template:
                        legacy.find((item) => item.code === 'pod_template')?.val ??
                        'pod_template_01',
                    title: selectExam?.label ?? '',
                    // title: 'timeout',
                    subject: publishData?.publishInfoDetail?.curriSubjectVal ?? '',
                    class:
                        (publishData?.publishInfoDetail?.curriGradeVal &&
                        publishData?.publishInfoDetail?.curriGradeVal !== '학년 정보 없음'
                            ? publishData?.publishInfoDetail?.curriGradeVal
                            : '') +
                        ' ' +
                        (publishData?.publishInfoDetail?.curriSemesterVal &&
                        publishData?.publishInfoDetail?.curriSemesterVal !== '학기 정보 없음'
                            ? publishData?.publishInfoDetail?.curriSemesterVal
                            : ''),
                },
                answer: legacy.find((item) => item.code === 'pod_answer')?.val ?? 'Y',
                description: legacy.find((item) => item.code === 'pod_description')?.val ?? 'Y',
                eng_description:
                    legacy.find((item) => item.code === 'pod_eng_description')?.val ?? 'Y',
                qr: 'N',
                letter:
                    legacy.find((item) => item.code === 'pod_letter')?.val.split('_')[2] ?? 'A4',
                section:
                    legacy.find((item) => item.code === 'pod_section')?.val.split('_')[2] === '1'
                        ? '1'
                        : '2_' +
                          legacy.find((item) => item.code === 'pod_section')?.val.split('_')[3],
                articles: uniqueItems ?? '',
                mebmerId: sUserId,
            };

            setQueryData(newData);
        }
    }, [publishData?.publishInfoDetail?.podOptionList, selectExam, articleIdList]);

    const backUrl = '/sets/pod';
    const backSearch = {
        publishGrpIdParam: publishGrpId,
        publishId: data?.examList[0]?.publishList[0]?.publishId,
        podData: dataParam ?? undefined,
    };

    const exitUrl = '/sets/published-box/detail/' + data?.examList[0]?.publishList[0]?.publishId;

    return (
        <>
            <PodHeader
                type={'detail'}
                queryData={queryData!}
                backUrl={backUrl}
                backSearch={backSearch}
                wsRef={wsRef}
                exitUrl={dataParam ? undefined : exitUrl}
            />
            <PodPreview
                wsRef={wsRef}
                queryData={queryData!}
                selectExam={selectExam}
                setSelectExam={setSelectExam}
                examList={examList}
                setArticleIdList={setArticleIdList}
                publishData={publishData}
            />
        </>
    );
}
